import type { NewAnalysisFormData } from "Forms/Schemas/newAnalysisFormSchema";
import {
  type CreateLocalClientBody,
  CreateLocalClientBodyType,
  type CreateMangoClientBody,
  CreateMangoClientBodyType,
} from "Infrastructure/ApiZfp/Schema";
import { parseBirthNumber } from "Infrastructure/Utils/ClientUtils";
import { format } from "date-fns";

// A Mango client card takes the address only as a whole - once any part is filled in, these can't be left out.
const MANGO_ADDRESS_REQUIRED_FIELDS = ["houseNumber", "city", "postalCode"] as const;

const hasAddress = (data: NewAnalysisFormData): boolean =>
  [data.street, data.houseNumber, data.streetNumber, data.city, data.postalCode].some(Boolean);

const toBirthNumber = (value: string | null | undefined): string => parseBirthNumber(value)?.formatted ?? value ?? "";

export const getMissingMangoAddressFields = (data: NewAnalysisFormData): (typeof MANGO_ADDRESS_REQUIRED_FIELDS)[number][] =>
  hasAddress(data) ? MANGO_ADDRESS_REQUIRED_FIELDS.filter((field) => !data[field]) : [];

export const toCreateLocalClientBody = (data: NewAnalysisFormData): CreateLocalClientBody => ({
  type: CreateLocalClientBodyType.local,
  name: data.firstName,
  surname: data.lastName,
  titlePrefix: data.titlePrefix || null,
  titlePostfix: data.titlePostfix || null,
  birthDate: data.birthDate instanceof Date ? data.birthDate.toISOString() : data.birthDate || null,
  email: data.email,
  phonePrefix: data.prefix,
  phoneNationalNumber: data.phone,
  addressStreet: data.street || null,
  addressStreetNumber: data.streetNumber || null,
  addressHouseNumber: data.houseNumber || null,
  addressCity: data.city || null,
  addressPostalCode: data.postalCode || null,
  addressCountry: data.countryCode || null,
});

export const toCreateMangoClientBody = (data: NewAnalysisFormData): CreateMangoClientBody => ({
  type: CreateMangoClientBodyType.mango,
  firstName: data.firstName,
  lastName: data.lastName,
  titleBefore: data.titlePrefix || null,
  titleAfter: data.titlePostfix || null,
  // A plain calendar date - toISOString() would shift a local midnight to the previous day in UTC.
  birthDate: format(data.birthDate, "yyyy-MM-dd"),
  personalIdentificationNumber: toBirthNumber(data.personalIdentificationNumber),
  email: data.email,
  phone: `${data.prefix}${data.phone}`,
  address: hasAddress(data)
    ? {
        street: data.street ?? "",
        descriptionNumber: data.houseNumber ?? "",
        orientationNumber: data.streetNumber || null,
        city: data.city ?? "",
        postalCode: data.postalCode ?? "",
        countryCode: data.countryCode ?? "",
      }
    : null,
});
